import React, { FC } from "react";
import HeroSearchForm from "components/HeroSearchForm/HeroSearchForm";
import imagePng from "images/nomadroof-main-picture-2026.png";
import { Link } from "react-router-dom";

export interface SectionHeroProps {
  className?: string;
  featuredProperties?: any[];
}

const SectionHero: FC<SectionHeroProps> = ({
  className = "",
  featuredProperties = [],
}) => {
  const displayProperties = featuredProperties?.slice(0, 4) || [];

  return (
    <section
      className={`nc-SectionHero relative bg-white dark:bg-neutral-900 overflow-hidden ${className}`}
      data-nc-id="SectionHero"
    >
      {/* SUBTLE GRADIENT ACCENTS */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-orange-50 via-transparent to-transparent opacity-60 rounded-full blur-3xl dark:from-orange-900/20"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-50 via-transparent to-transparent opacity-50 rounded-full blur-3xl dark:from-blue-900/20"></div>

      {/* MAIN CONTENT CONTAINER */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* LEFT SIDE CONTENT */}
          <div className="space-y-5 sm:space-y-6 lg:space-y-8">

            {/* BADGE */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-orange-200 dark:border-orange-700">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
              <span className="text-xs sm:text-sm font-semibold text-orange-700 dark:text-orange-400">
                Premium Student Housing
              </span>
            </div>

            {/* HEADLINE */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] tracking-tight text-gray-900 dark:text-white">
                Your Semester
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-orange-600 via-orange-500 to-pink-500">
                    Abroad Starts Here
                  </span>
                  <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                    <path d="M2 10C50 5 100 2 150 4C200 6 250 8 298 6" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round" />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#F97316" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#EC4899" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
                Booking with us is just the beginning — the real journey starts
                when you become part of our community.
              </p>
            </div>

            {/* SEARCH BAR - Desktop only */}
            <div className="hidden lg:block pt-2">
              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg border border-gray-100 dark:border-neutral-700 p-2 hover:shadow-xl transition-shadow">
                <HeroSearchForm />
              </div>
            </div>

            {/* STATS */}
            <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 pt-1 sm:pt-2">
              <div className="space-y-1">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600">
                  500+
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium">
                  Happy Students
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-pink-600">
                  15+
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium">
                  Properties
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-600">
                  4.9★
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium">
                  Average Rating
                </div>
              </div>
            </div>

            {/* POPULAR DESTINATIONS */}
            <div className="space-y-2.5 sm:space-y-3 pt-1 sm:pt-2">
              <p className="font-semibold text-gray-700 dark:text-gray-300 text-xs sm:text-sm uppercase tracking-wider">
                Popular Student Houses & Apartments
              </p>

              {displayProperties.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {displayProperties.map((property: any) => (
                    <Link
                      key={property.id}
                      to={`/listing-stay-detail/${property.slug || property.id}`}
                      className="bg-white dark:bg-neutral-800 hover:bg-gradient-to-r hover:from-orange-500 hover:to-pink-500 border-2 border-gray-200 dark:border-neutral-600 hover:border-transparent px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl font-medium text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:text-white transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105"
                    >
                      {property.title}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {["Casa Madrid", "Casa Pisco", "Casa Rio", "Depa Uyuni"].map((destination) => (
                    <div
                      key={destination}
                      className="bg-gray-100 dark:bg-neutral-800 border-2 border-gray-200 dark:border-neutral-700 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl font-medium text-xs sm:text-sm text-gray-400 animate-pulse"
                    >
                      {destination}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDE IMAGE */}
          <div className="relative mt-6 sm:mt-8 lg:mt-0 mb-12 sm:mb-16 lg:mb-20">
            <div className="absolute -top-4 -right-4 w-48 h-48 sm:w-72 sm:h-72 bg-gradient-to-br from-orange-100 to-pink-100 dark:from-orange-900/20 dark:to-pink-900/20 rounded-full opacity-20 blur-3xl"></div>

            <div className="relative">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl ring-1 ring-gray-200 dark:ring-neutral-700">
                <img
                  className="w-full h-auto object-cover object-center"
                  src={imagePng}
                  alt="Beautiful student accommodation in Lima, Peru"
                  style={{ maxHeight: "600px", minHeight: "400px" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {/* Floating info card */}
              <div className="absolute -bottom-6 left-3 right-3 sm:-bottom-8 sm:left-4 sm:right-4 lg:-bottom-10 lg:left-6 lg:right-6 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-xl border border-gray-100 dark:border-neutral-700">
                <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-[10px] sm:text-xs lg:text-sm text-gray-500 dark:text-gray-400 font-medium mb-0.5">
                        Our Properties in Lima
                      </div>
                      <div className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">
                        Exclusive Listings
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/exclusive"
                    className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-3 py-1.5 sm:px-5 sm:py-2.5 lg:px-6 lg:py-3 rounded-lg sm:rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105 text-[11px] sm:text-sm lg:text-base whitespace-nowrap"
                  >
                    View All
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SCROLL INDICATOR */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block">
        <div className="flex flex-col items-center space-y-2 text-gray-400 dark:text-gray-500">
          <span className="text-xs font-medium">Scroll to explore</span>
          <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default SectionHero;